import React from 'react';
import { BarChart3, ShoppingCart, Layout, Layers, Cpu, Globe, ShieldCheck, Settings, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import VisualCanvas from './VisualCanvas';

const SpecializationSection = () => {
    const specializations = [
        { title: "Fintech & Loans", icon: BarChart3, color: "text-emerald-500" },
        { title: "Digital Product Platforms", icon: ShoppingCart, color: "text-blue-500" },
        { title: "Property Portals", icon: Layout, color: "text-indigo-500" },
        { title: "CRM & ERP Systems", icon: Layers, color: "text-sky-500" },
        { title: "Social Automation Tools", icon: Cpu, color: "text-rose-500" },
        { title: "Live Streaming Tools", icon: Globe, color: "text-violet-500" },
        { title: "NGO & Political Portals", icon: ShieldCheck, color: "text-slate-800" },
        { title: "Custom Admin Systems", icon: Settings, color: "text-orange-500" },
    ];

    const timelineModels = [
        { type: "Basic Website", duration: "7–10 Days", speed: "High", complexity: "Low" },
        { type: "Business Website", duration: "15–20 Days", speed: "Medium", complexity: "Medium" },
        { type: "Advanced Web App", duration: "25–40 Days", speed: "Stable", complexity: "High" },
        { type: "Mobile Application", duration: "30–45 Days", speed: "Native", complexity: "Ultra" },
    ];

    return (
        <section className="py-24 md:py-32 min-h-screen flex items-center bg-white transition-colors duration-500 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] -z-1" />

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-primary font-black uppercase tracking-[0.4em] text-[10px] md:text-xs mb-4 block">Our Spectrum</span>
                        <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-8 leading-tight">Expertise Areas where we <span className="text-gradient">Excel</span></h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                            {specializations.map((spec, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                                    whileHover={{ x: 10, backgroundColor: 'rgba(37, 99, 235, 0.05)' }}
                                    className="p-3.5 md:p-5 bg-slate-50 rounded-2xl md:rounded-[1.5rem] border border-slate-100 flex items-center gap-3 md:gap-5 transition-all group relative"
                                >
                                    <div className={`shrink-0 ${spec.color} group-hover:scale-110 transition-transform`}>
                                        <spec.icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                                    </div>
                                    <span className="font-bold text-slate-900 text-sm md:text-lg tracking-tight">{spec.title}</span>
                                    <span className="absolute right-4 top-2 text-[9px] md:text-[10px] font-black text-slate-200 uppercase tracking-widest">
                                        {(idx + 1).toString().padStart(2, '0')}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <div className="relative">
                        <div className="absolute inset-0 z-0">
                            <VisualCanvas type="torus" color="#2563eb" height="500px" />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="grid grid-cols-2 gap-4 md:gap-6 relative z-10"
                        >
                            {timelineModels.map((model, idx) => (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -10 }}
                                    className="p-6 md:p-8 bg-white border border-slate-100 rounded-[1.5rem] md:rounded-[2.5rem] group hover:border-primary/50 transition-all shadow-xl hover:shadow-2xl"
                                >
                                    <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-blue-50 flex items-center justify-center text-primary mb-6 md:mb-8 group-hover:bg-primary group-hover:text-white transition-all">
                                        <Clock className="w-6 h-6 md:w-8 md:h-8" />
                                    </div>
                                    <h4 className="font-black text-slate-900 text-xs md:text-lg mb-1.5 uppercase tracking-wide">{model.type}</h4>
                                    <p className="text-2xl md:text-4xl font-black text-primary mb-1.5 md:mb-3 tabular-nums">{model.duration.split('–')[0]}<span className="text-xs md:text-base font-bold text-slate-400 uppercase tracking-widest ml-1">days+</span></p>
                                    <p className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">{model.speed} Execution</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SpecializationSection;
